"use client";

import React from "react";
import { Phone, MessageCircle, Sparkles, ShieldCheck, Flame, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Link } from "@/components/common/Link";
import { PHONE_LOCAL, PHONE_TEL_HREF, WHATSAPP_HREF } from "@/lib/contact";
export default function OvenLandingHero() {
  const benefits = [{
    icon: Flame,
    title: "إذابة الدهون المستعصية",
    description: "استخدام منظفات متخصصة ومصرحة لتفكيك الزيوت والدهون المتراكمة وإعادة لمعان الأفران."
  }, {
    icon: Sparkles,
    title: "تعقيم وتلميع شامل",
    description: "تنظيف مقصورة الطهي الداخلية، الزجاج المزدوج، الشعلات، والأسطح الخارجية بدرجة أمان عالية."
  }, {
    icon: ShieldCheck,
    title: "فحص أمان الشعلات والأنابيب",
    description: "معاينة عيون الغاز ومداخل الإشعال لضمان عمل الشعلة بكفاءة واستقرار تام بعد الغسيل."
  }, {
    icon: Clock,
    title: "خدمة منزلية سريعة بالرياض",
    description: "فريق فني متخصص يصلك لباب بيتك بكامل المعدات في مختلف أحياء الرياض دون عناء النقل."
  }];
  return <section id="ovencleaninglandinghero" data-nav="light" dir="rtl" className="relative bg-background text-foreground py-16 md:py-24 overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Hero Banner Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 flex flex-col items-start space-y-6">
            <Badge variant="outline" className="text-secondary border-border bg-muted/60 px-3 py-1 text-sm font-medium">
              خدمة منزلية معتمدة داخل الرياض
            </Badge>

            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight tracking-tight text-foreground">
              أفضل خدمة <span className="text-primary">تنظيف أفران بالرياض</span> باحترافية وأمان تام
            </h1>

            <p className="text-muted-foreground text-base sm:text-lg leading-relaxed max-w-2xl">
              تقدم شركة لمسة احتراف حلولاً متكاملة لصيانة وغسيل الأفران المنزلية بالرياض. نقضي على أصعب الدهون المحروقة، ونعقم مقصورة الخبز، مع تنظيف دقيق للعيون والزجاج والشوايات ليعود فرنك كالجديد تماماً.
            </p>

            <div className="flex flex-wrap items-center gap-4 pt-2 w-full sm:w-auto">
              <Button asChild size="lg" className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-6 shadow-sm">
                <a href={PHONE_TEL_HREF} className="inline-flex items-center gap-2">
                  <Phone className="size-5" />
                  <span>اتصل الآن: {PHONE_LOCAL}</span>
                </a>
              </Button>

              <Button asChild size="lg" variant="outline" className="border-border hover:bg-accent text-foreground font-medium px-6">
                <Link to={WHATSAPP_HREF} newTab className="inline-flex items-center gap-2">
                  <MessageCircle className="size-5 text-primary" />
                  <span>تواصل عبر واتساب</span>
                </Link>
              </Button>
            </div>
          </div>

          <div className="lg:col-span-5 relative">
            <div className="relative rounded-2xl overflow-hidden shadow-xl border border-border bg-card">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1828342/img/oven_cleaning_riyadh_hero.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1828342/img/oven_cleaning_riyadh_hero.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1828342/img/oven_cleaning_riyadh_hero.webp-wvc-sizes" alt="تنظيف أفران بالرياض بشركة لمسة احتراف" loading="lazy" decoding="async" className="w-full h-[360px] sm:h-[420px] object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent flex items-end p-6">
                <p className="text-white text-sm sm:text-base font-medium">
                  غسيل وإزالة دهون بالبخار ومواد متخصصة لكافة أنواع الأفران والغاز
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Benefit Cards Section */}
        <div className="mt-20">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <h2 className="text-2xl sm:text-3xl font-bold text-foreground">
              مميزات خدمة تنظيف الأفران المنزلية
            </h2>
            <p className="text-muted-foreground mt-2 text-sm sm:text-base">
              نضمن لك نظافة عميقة وإطالة عمر جهازك بمواد فعالة وآمنة تماماً على صحة عائلتك.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit, i) => {
            const IconComp = benefit.icon;
            return <Card key={i} data-index={i} className="border border-border/80 shadow-xs hover:shadow-md transition-shadow duration-200">
                  <CardHeader className="gap-3">
                    <div className="size-12 rounded-lg bg-primary/10 flex items-center justify-center text-primary">
                      <IconComp className="size-6" />
                    </div>
                    <CardTitle className="text-lg font-semibold text-foreground">
                      {benefit.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {benefit.description}
                    </p>
                  </CardContent>
                </Card>;
          })}
          </div>
        </div>

        {/* Closing CTA Band */}
        <div className="mt-16 rounded-2xl bg-secondary text-secondary-foreground p-8 sm:p-12 border border-secondary/20 shadow-md">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-8 text-center lg:text-right">
            <div className="space-y-3 max-w-xl">
              <h3 className="text-2xl sm:text-3xl font-bold text-white">
                جاهزون لخدمتكم فوراً في جميع أحياء الرياض
              </h3>
              <p className="text-white/80 text-sm sm:text-base leading-relaxed">
                احجز موعد تنظيف الفرن المنزلي اليوم مع لمسة احتراف واستمتع بنظافة فائقة وجودة عمل لا تضاهى.
              </p>
            </div>

            <div className="flex flex-wrap justify-center items-center gap-4 shrink-0">
              <Button asChild size="lg" className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-6 shadow-sm">
                <a href={PHONE_TEL_HREF} className="inline-flex items-center gap-2">
                  <Phone className="size-5" />
                  <span>{PHONE_LOCAL}</span>
                </a>
              </Button>
              <Button asChild size="lg" variant="outline" className="border-white/30 text-white bg-transparent hover:bg-white/10 px-6">
                <Link to={WHATSAPP_HREF} newTab className="inline-flex items-center gap-2">
                  <MessageCircle className="size-5 text-white" />
                  <span>مراسلة واتساب</span>
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}